import type { Messages } from '../messages.js'

/**
 * Icelandic. `leikur` for flips, the word an Icelandic board game uses for a move.
 *
 * `orð` and `stig` do not change in the plural — eitt orð, tvö orð — which is why two of these
 * pairs carry the same string on purpose.
 */
export const is: Messages = {
  tag: 'is',

  readingDictionary: 'Les orðabókina…',
  noWordList: 'Enginn orðalisti fyrir „{language}“. Búðu hann til:  pnpm dictionary build',
  emptyWordList: 'Orðalistinn fyrir „{language}“ er tómur.',

  flips: 'leikir',
  score: 'stig',
  words: 'orð',
  round: 'umferð',
  ticksLeftLabel: 'Tími eftir í þessari umferð',
  typeAWord: 'sláðu inn orð',
  tapPrompt: 'ýttu á stafi til að velja eða skila, síðan {action}',

  boardOfTiles: 'Borð með {n} flísum',
  faceDown: 'á hvolfi',
  wildCard: 'jóker',
  wildKey: 'hvaða stafur sem er',
  letterReplaced: '{from} varð að {to}',
  letterSwap: 'STAFASKIPTI!',
  spentTile: 'notuð flís',
  hiddenWhilePaused: 'falið á meðan gert er hlé',
  letterInWord: '{letter}, {position}. stafur orðsins',

  completeWord: 'Ljúka orði',

  completeShort: 'Lokið',
  reset: 'Hreinsa',
  pause: 'Hlé',
  resume: 'Halda áfram',
  newGame: 'Nýr leikur',
  paused: 'Í hléi',
  outOfFlips: 'Leikir búnir',
  finalResult: '{score} fyrir {words} á {rounds}',
  playAgain: 'Spila aftur',
  share: 'Deila',
  shareCopied: 'Afritað.',
  shareSelect: 'Afritaðu þetta:',

  lettersSelect: 'stafir velja',
  keysWild: 'tekinn þegar þú slærð inn staf sem engin flís sýnir',
  clearsEvery: 'hreinsar alla valda {letter}',
  undoLastLetter: 'afturkalla síðasta staf',
  noWordsYet: 'Engin orð enn.',

  wordRejected: '{word}  {reason}',
  reasonDuplicate: 'þegar fundið',
  reasonTooShort: 'of stutt',
  reasonNotAWord: 'ekki orð',
  reasonAllFound: 'þú ert þegar með þau öll',
  noSuchLetterUp: 'enginn {letter} snúinn upp',
  nothingUp: 'ekkert snúið upp',
  letterHidden: 'stafur faldi sig',
  shuffled: 'stokkað',
  shuffledAndBilled: 'stokkað, {flips} ónotaðir dregnir frá',
  tooFewLetters: 'Of fáir stafir eftir',

  gameLanguage: 'tungumál',
  interfaceLanguage: 'viðmót',
  dictionarySize: '{common} algeng af {full} orðum',
  filterLanguages: 'Leita að tungumáli',
  noMatches: 'Ekkert fannst',

  nerdMode: 'nördastilling',
  rules: 'Reglur',
  difficulty: 'erfiðleikastig',
  difficultyNames: { easy: 'létt', medium: 'miðlungs', hard: 'erfitt', insane: 'brjálað' },
  pausedNoBoard: 'Þessi leikur kemst ekki á stigatöfluna.',
  about: 'Um',
  themeLabel: 'þema',
  themeNames: { traditional: 'hefðbundið', light: 'ljóst', contrast: 'mikil birtuskil' },
  tiles: 'flísar (N)',
  secondsPerTick: 'sekúndur / takt',
  holdTicks: 'biðtaktar',
  minWord: 'stysta orð',
  startingFlips: 'leikir í upphafi',
  wildChance: 'líkur á jóker',
  replaceChance: 'líkur á stafaskiptum',
  hideChance: 'líkur á að stafur hverfi',
  wordCompleteMode: 'þegar orði er lokið',
  wordCompleteNames: { shuffle: 'stokka', spend: 'eyða', keep: 'halda' },
  flipEconomy: 'endurgreiðsla leikja',
  flipEconomyNames: {
    none: 'engin',
    perLetter: 'á staf',
    fibonacci: 'fibonacci',
    overMinimum: 'yfir lágmarki',
  },
  repeatedLetterKey: 'lykill fyrir endurtekinn staf',
  keySchemeNames: { cycle: 'hringur', advance: 'áfram' },
  keySchemeHelp: {
    cycle:
      'A tekur næsta ónotaða A, og þegar þeir eru allir komnir í orðið hreinsar það þá. ' +
      'Shift+A hreinsar þá líka.',
    advance: 'A tekur næsta ónotaða A. Shift+A hreinsar alla A úr orðinu.',
  },

  whatThatMeans: 'Hvað það þýðir',
  factRound: 'umferð',
  factWholeBoardUp: 'allt borðið uppi í',
  factRoundCosts: 'umferð kostar',
  factFlipsBuy: 'upphafsleikir kaupa',
  factThisBoard: 'þetta borð',
  factBoardHadToAdmit: 'borðið varð að leyfa',
  ticksAndSeconds: '{ticks}, {seconds}',
  wordsLongest: '{words}, lengst {longest}',
  wordsIncludingOneOf: '{words}, þar af eitt með {ceiling}',
  scorelessRounds: '{rounds} án stiga',

  whatAWordPays: 'Hvað orð gefur',
  columnLetters: 'stafir',
  columnCost: 'kostnaður',
  columnPoints: 'stig',
  columnFlips: 'leikir',
  columnNet: 'nettó',

  canonicalRules: 'Venjulegar reglur fyrir {difficulty}.',
  customRules: 'Breytt frá forstillingu. Stig með eigin reglum komast ekki á listann.',
  applyAndStart: 'Virkja og byrja nýjan leik',
  changesNextGame: 'Breytingarnar taka gildi í næsta leik.',
  presets: 'Forstillingar:',

  start: 'Byrja',
  restart: 'Upp á nýtt',
  quit: 'Hætta',
  quitTitle: 'Hætta í þessum leik?',
  restartTitle: 'Byrja þennan leik upp á nýtt?',
  restartConfirm: 'Upp á nýtt',
  quitConfirm: 'Hætta',
  keepPlaying: 'Halda áfram að spila',
  personalBest: 'Bestu leikirnir þínir',
  newPersonalBest: 'Nýtt persónulegt met.',
  columnRank: '#',
  notRanked: 'Eigin reglur, svo þessi leikur kemst ekki á listann.',
  notRankedPaused: 'Tíminn stöðvaðist, svo þessi leikur kemst ekki á listann.',
  rankOfTotal: '{rank} af {total}',

  howToPlay: 'Hvernig á að spila',

  backToGame: 'Aftur í leikinn',
  welcomeTitle: 'Velkomin í Blinkered',
  tutorialSkip: 'Sleppa',
  tutorialNext: 'Áfram',
  tutorialBack: 'Til baka',
  tutorialStart: 'Byrja að spila',
  tutorialHideAgain: 'Ekki sýna þetta aftur',
  tutorialProgress: '{n} af {total}',
  tutorialSkipTitle: 'Sleppa kynningunni?',
  tutGoalTitle: 'Blinkered er kapphlaup',
  tutGoalBody:
    'Þú byrjar leikinn með ákveðinn fjölda leikja, og hver flís sem snýst kostar einn leik. Þegar leikirnir klárast er leiknum lokið. Orð gefa leiki, og því lengra sem orðið er, því fleiri leiki færðu.',
  tutSpentBody:
    'Stafir sem þú notar í orð snúa niður fram að næstu umferð. Aðeins stafaskipti breyta því hvaða stafir eru á borðinu.',
  tutChangeTitle: 'Stafir geta breyst',
  tutSameLetters:
    'Sástu orð sem þú náðir ekki að spila? Sömu stafirnir koma aftur í næstu umferð, stokkaðir á nýja staði.',
  tutPickLetters: 'Ýttu á stafina sem þú vilt, í röð, til að mynda orð.',
  tutMoreTurn:
    'Flísar halda áfram að snúast meðan þú hugsar, svo betri stafur gæti enn verið á leiðinni.',
  tutTapBack:
    'Tókstu einn sem þú vildir ekki? Ýttu aftur á hann til að skila honum. Hvaða sem er, ekki bara þann síðasta.',
  tutComplete: 'Ýttu á Lokið þegar orðið er tilbúið.',
  htBoardTitle: 'Borðið',
  htBoardBody:
    'Flísarnar snúast við ein í einu, í tilviljanakenndri röð. Úr þeim sem snúa upp má mynda orð.',
  htWordsTitle: 'Orðin',
  htWordsBody:
    'Myndaðu orð úr flísunum sem snúa upp með því að slá inn eða smella á stafina í röð.',
  htFlipsTitle: 'Leikirnir',
  htFlipsBody:
    'Hver flís sem snýst kostar leik. Fullgert orð skilar leikjum til baka, og lengri orð skila fleirum. Þegar leikirnir klárast er leiknum lokið.',
  htRoundTitle: 'Umferðin',
  htRoundBody:
    'Þegar síðasta flís umferðarinnar snýst stendur allt borðið kyrrt andartak. Svo er flísunum snúið við og stokkað, og ný umferð hefst.',
  htLanguagesTitle: 'Tungumálin',
  htLanguagesBody:
    'Þau eru {n}. Hvert borð má leysa með orðum sem fólk notar í raun. Sjaldgæft orð gefur líka stig, ef orðabókin þekkir það.',
  htKeysTitle: 'Lyklaborðið',
  htWildTitle: 'Jókerarnir',
  htWildBody:
    'Stundum birtist jóker í stað stafs. Jóker gildir sem hvaða stafur sem myndar gilt orð. Orð sem þegar er fundið gildir ekki.',
  htHideTitle: 'Stafir sem fela sig',
  htHideBody:
    'Á erfiðari stigum getur stafur sem er uppi snúist við af sjálfu sér og komið aftur síðar í umferðinni. Tímamælirinn fær auka tikk fyrir það, í rauðu. Stafir sem þú hefur þegar valið snúast ekki við.',
  tutHideBody:
    'Á erfiðari stigum getur stafur sem er uppi snúist við af sjálfu sér og komið aftur síðar í umferðinni. Stafir sem þú hefur þegar valið snúast ekki við.',
  htSwapTitle: 'Stafir sem breytast',
  htSwapBody:
    'Stundum er einum staf skipt út fyrir annan milli umferða. Þú sérð hvor hvarf og hvor kom.',
  htLevelsTitle: 'Stigin',
  htLevelEasy:
    'Sömu tólf stafirnir allan leikinn, svo þú getur lært þá og borið orðalista í höfðinu. Flísarnar snúast hægt og allt borðið sést nógu lengi til að ljúka valinu.',
  htLevelMedium:
    'Öðru hverju breytist stafur, svo erfiðara verður að muna orð sem geymd voru til seinni tíma. Minni tími til að horfa og minni til að hugsa.',
  htLevelHard:
    'Þriggja stafa orð hætta að gilda og stafur breytist um það bil aðra hverja umferð. Borðið nær varla að sýna sig áður en það stokkast.',
  htLevelInsane: 'Allt í einu, á fullri ferð. Borðið stokkast nánast strax eftir síðasta leikinn.',
  htTouchTitle: 'Snertiskjárinn',
  htTouchBody:
    'Ýttu á flís sem snýr upp til að taka stafinn hennar. Ýttu á staf sem þú hefur tekið til að skila honum. Lokið og Hreinsa eru undir borðinu.',

  accountTitle: 'Aðgangurinn þinn',
  menuProfile: 'Prófíllinn minn',
  menuGames: 'Leikirnir mínir',
  menuPublicPage: 'Opinbera síðan mín',
  menuSignedInAs: 'Skráð(ur) inn sem',
  signIn: 'Skrá inn',
  signOut: 'Skrá út',
  signInTitle: 'Skráðu þig inn eða nýskráðu þig',
  signInLead: 'Aðgangur geymir leikina þína, í öllum tækjum.',
  signInKeepGame: 'Skráðu þig inn til að halda þessum leik, og öllum eftir hann.',
  continueWithApple: 'Halda áfram með Apple',
  continueWithGoogle: 'Halda áfram með Google',
  signInOr: 'eða',
  emailLabel: 'Netfang',
  codeLabel: 'Kóði',
  emailMeACode: 'Sendið mér kóða',
  working: 'Augnablik…',
  sendNewCode: 'Senda nýjan kóða',
  codeSent: 'Kíktu í póstinn. Kóðinn gildir í 10 mínútur.',
  notNow: 'Ekki núna',
  badEmail: 'Þetta lítur ekki út eins og netfang.',
  badCode: 'Þessi kóði virkaði ekki. Kóðar renna út eftir 10 mínútur og gilda einu sinni.',
  serverBusy: 'Náði ekki í þjóninn. Reyndu aftur eftir augnablik.',
  serverDown: 'Náði ekki í þjóninn.',
  offline: 'Ónettengt',
  leaderboardTitle: 'Stigatafla',
  leaderboardEmpty: 'Engir leikir á þessari töflu enn. Verðu fyrstur.',
  leaderboardWouldBe: 'Hvar þessi leikur myndi standa',
  leaderboardThisGame: 'Þessi leikur',
  leaderboardRanksHere: 'Hvar þessi leikur stendur',
  leaderboardOutside: 'Ekki meðal fimm efstu.',
  tutAccountTitle: 'Haltu stigunum þínum',
  tutAccountBody:
    'Reikningur vistar leikina þína, setur stigin þín á stigatöfluna og heldur sniðinu þínu.',
  ssoCancelled: 'Hætt við innskráningu.',
  ssoExpired: 'Þetta tók of langan tíma. Skráðu þig inn aftur.',
  ssoNoUsername: 'Náði ekki að klára að stofna aðganginn. Reyndu aftur eftir augnablik.',
  ssoFailed: 'Þetta virkaði ekki. Reyndu aftur, eða notaðu netfangið þitt.',
  tabProfile: 'Prófíll',
  countryAny: 'Segi það ekki',
  findCountry: 'Leita að löndum',
  usernameLabel: 'Notandanafn',
  bioLabel: 'Um mig',
  countryLabel: 'Land',
  bioHint: 'Engir tenglar. {left}/{max}',
  save: 'Vista',
  saving: 'Vistar…',
  saved: 'Vistað.',
  nameTaken: 'Einhver á þetta nafn nú þegar.',
  nameTooShort: 'Aðeins lengra, takk.',
  nameTooLong: 'Þetta er of langt.',
  nameBadCharacters: 'Aðeins bókstafir, tölustafir, bandstrik og undirstrik.',
  nameBadEdges: 'Verður að byrja og enda á bókstaf eða tölustaf.',
  nameMixedScripts: 'Veldu eitt stafróf og haltu þig við það.',
  nameReserved: 'Þetta líkist nafni sem við úthlutum sjálf. Veldu annað.',
  nameUnusable: 'Ekki er hægt að nota þetta nafn.',
  bioTooLong: 'Hér komast mest {max} stafir fyrir.',
  bioHasLink: 'Tenglar eru ekki leyfðir hér.',
  bioHasControl: 'Þetta inniheldur stafi sem komast ekki hér fyrir.',
  bioUnusable: 'Ekki er hægt að nota þennan texta.',
  gamesHeading: 'Leikir',
  gamesEmpty: 'Ekkert hér enn. Leikir sem þú spilar innskráð(ur) birtast hér.',
  gamesEmptyShort: 'Ekkert hér enn.',
  gamesLoading: 'Les leikina þína…',
  theirGamesLoading: 'Les leikina þeirra…',
  profileLoading: 'Les prófílinn…',
  playerNotFound: 'Hér er enginn með þessu nafni.',
  columnWhen: 'Hvenær',
  columnRounds: 'umferðir',
  columnGame: 'Leikur',
  gameLoading: 'Les leikinn…',
  gameUnreadable: 'Ekki tókst að lesa þennan leik.',
  gameNotRanked: 'Spilaður með breyttum reglum, svo hann telur ekki.',
  detailNotKept: 'Orðin og borðin í þessum leik eru ekki lengur geymd.',
  openingBoard: 'Upphafsborðið',
  roundByRound: 'Umferð fyrir umferð',
  roundNothing: 'ekkert fannst',
  changeReplaced: '{letter} skipt út í reit {slot}',
  changeWildOn: '{letter} jóker í reit {slot}',
  changeWildOff: '{letter} jóker farinn úr reit {slot}',
  keepThisGame: 'Halda þessum leik',
  savedToYourGames: 'Vistað hjá leikjunum þínum.',

  reportThis: 'Tilkynna',
  reportHeading: 'Tilkynna þetta',
  reportTheName: 'Nafnið',
  reportTheBio: 'Kynningin',
  reportTheScore: 'Skorið',
  reportWhy: 'Hvað er að því? Valfrjálst.',
  reportSend: 'Senda tilkynningu',
  reportSent: 'Þakka þér. Einhver skoðar það.',
  reportSignIn: 'Skráðu þig inn til að tilkynna eitthvað.',
  reportGone: 'Það er ekki þar lengur.',
  reportCancel: 'Hætta',

  deleteAccount: 'Eyða reikningnum mínum',
  deleteAccountWhat:
    'Þetta fjarlægir notandasíðuna þína og alla leiki sem þú hefur haldið, endanlega. Því er ekki hægt að hnekkja.',
  deleteAccountAsk: 'Sendu mér kóða',
  deleteConfirm: 'Eyða endanlega',
  deleteGone: 'Reikningurinn þinn er farinn. Þakka þér fyrir að spila.',
  deleteNoAddress:
    'Það er ekkert staðfest netfang á þessum reikningi, svo ekki er unnt að staðfesta eyðingu. Hafðu samband og við gerum það handvirkt.',

  rulesetFacts: 'Orð með {letters} · ný flís á {seconds} fresti',
  plurals: {
    words: { one: '{n} orð', other: '{n} orð' },
    rounds: { one: '{n} umferð', other: '{n} umferðir' },
    flips: { one: '{n} leikur', other: '{n} leikir' },
    ticks: { one: '{n} taktur', other: '{n} taktar' },
    points: { one: '{n} stig', other: '{n} stig' },
    minLetters: { one: 'minnst {n} staf', other: 'minnst {n} stöfum' },
    seconds: { one: '{n} sekúndu', other: '{n} sekúndna' },
  },
}
